import { useTranslation } from 'react-i18next';
import { ReactComponent as ResetPass } from '../static/ic_password.svg';
import { ReactComponent as UserAvatar } from '../static/ic_avatar.svg';
import { ReactComponent as AddUser } from '../static/ic_add_user.svg';
import { ReactComponent as SubmitIcon } from '../static/ic_submit.svg';
import { ReactComponent as SubmissionIcon } from '../../../static/icons/tasklists/ic_submissions.svg';
import { ReactComponent as PrinterIcon } from '../../../static/icons/ic_printer.svg';
import { ReactComponent as AssignIcon } from '../../../static/icons/ic_assign.svg';

import loginImage from '../../../assets/login.png';
import resetPassImage from '../../../assets/restPass.png';
import checkEmail from '../../../assets/checkEmail.png';
import forgotImage from '../../../assets/forgot.png';
import settings from '../../../assets/settings.jpg';
import dragZone from '../../../assets/dragZone.jpg';
import savePicture from '../../../assets/savePicture.jpg';
import teamPage from '../../../assets/teamPage.jpg';
import addOne from '../../../assets/addOne.jpg';
import fill from '../../../assets/fill.jpg';
import addTasklist from '../../../assets/addTasklist.jpg';
import tasklistPage from '../../../assets/tasklistPage.jpg';
import createTasklist from '../../../assets/createTasklist.jpg';
import clickSubmission from '../../../assets/clickSubmission.jpg';
import submission from '../../../assets/submission.jpg';
import submit from '../../../assets/submit.jpg';
import schedulePage from '../../../assets/schedulePage.jpg';
import schedulePrint from '../../../assets/schedulePrint.jpg';
import calendarTab from '../../../assets/calendarTab.jpg';
import calendarTime from '../../../assets/calendarTime.jpg';
import calendarPrint from '../../../assets/calendarPrint.jpg';
import ordersPage from '../../../assets/orderPage.jpg';
import unassignTab from '../../../assets/unassignTab.jpg';
import assignMember from '../../../assets/assignMember.jpg';
import saveOrders from '../../../assets/saveOrders.jpg';

import { TArticle, TArticleItem } from '../../../types';

export function useArticles() {
  const { t } = useTranslation('knowledgeBase');

  const articles: TArticle = {
    1: {
      title: t('howToReset'),
      subtitle: t('youCanChangePass'),
      steps: {
        one: {
          text: `${t(
            'goToTheLogin',
          )} <a href='/'>${t(
            'forgotPass',
          )}<a/>`,
          image: loginImage,
        },
        two: {
          text: t('confirmYourEmail'),
          image: forgotImage,
        },
        three: {
          text: t('youWillReceive'),
          image: checkEmail,
        },
        four: {
          text: t('resetYourPass'),
          image: resetPassImage,
        },
      },
    },
    2: {
      title: t('howDoISetUp'),
      subtitle: t('youCanChange'),
      steps: {
        one: {
          text: `${t(
            'goToThe',
          )} <a href='/settings'>${t(
            'settingsPage',
          )}<a/>`,
          image: settings,
        },
        two: {
          text: t('dragYourPhoto'),
          image: dragZone,
        },
        three: {
          text: t('clickSaveChangesBtn'),
          image: savePicture,
        },
      },
    },
    3: {
      title: t('howDoIAdd'),
      subtitle: t('youCanAdd'),
      steps: {
        one: {
          text: `${t(
            'goToThe',
          )} <a href='/team'>${t(
            'teamPage',
          )}<a/>`,
          image: teamPage,
        },
        two: {
          text: t('toAddMember'),
          image: addOne,
        },
        three: {
          text: t('fillInTheFields'),
          image: fill,
        },
      },
    },
    4: {
      title: t('howDoICreateATasklists'),
      subtitle: t('youCanCreateATasklist'),
      steps: {
        one: {
          text: `${t(
            'goToThe',
          )} <a href='/tasklists'>${t(
            'tasklistsPage',
          )}<a/>`,
          image: tasklistPage,
        },
        two: {
          text: t('clickOnTheAddTasklist'),
          image: addTasklist,
        },
        three: {
          text: t('fillInTheFieldsAndClick'),
          image: createTasklist,
        },
      },
    },
    5: {
      title: t('howDoISubmit'),
      subtitle: t('youCanSubmit'),
      steps: {
        one: {
          text: `${t(
            'goToThe',
          )} <a href='/tasklists'>${t(
            'tasklistsPage',
          )}<a/>`,
          image: tasklistPage,
        },
        two: {
          text: t('clickSubmission'),
          image: clickSubmission,
        },
        three: {
          text: t('selectTheTasklists'),
          image: submission,
        },
        four: {
          text: t('clickOnSubmitBtn'),
          image: submit,
        },
      },
    },
    6: {
      title: t('howDoIPrint'),
      subtitle: t('youCanPrint'),
      steps: {
        one: {
          text: `${t(
            'goToThe',
          )} <a href='/schedule'>${t(
            'schedulePage',
          )}<a/>`,
          image: schedulePage,
        },
        two: {
          text: t('toPrintWeekly'),
          image: schedulePrint,
        },
        three: {
          text: t('toPrintCalendar'),
          image: calendarTab,
        },
        four: {
          text: t('selectTime'),
          image: calendarTime,
        },
        five: {
          text: t('pressThePrintButton'),
          image: calendarPrint,
        },
      },
    },
    7: {
      title: t('howToAssign'),
      subtitle: t('youCanAssign'),
      steps: {
        one: {
          text: `${t(
            'goToThe',
          )} <a href='/orders'>${t(
            'ordersPage',
          )}<a/>`,
          image: ordersPage,
        },
        two: {
          text: t('clickTheUnassigned'),
          image: unassignTab,
        },
        three: {
          text: t('assignMembers'),
          image: assignMember,
        },
        four: {
          text: t('saveOrders'),
          image: saveOrders,
        },
      },
    },
  };

  const articleItems: Array<TArticleItem> = [
    {
      title: t('howDoIReset'),
      articleId: 1,
      icon: ResetPass,
      video: 'https://www.youtube.com/watch?v=J60xDzuuprw',
    },
    {
      title: t('howDoISetUp'),
      articleId: 2,
      icon: UserAvatar,
      video: 'https://www.youtube.com/watch?v=dB8TQcZodZo',
    },
    {
      title: t('howDoIAdd'),
      articleId: 3,
      icon: AddUser,
      video: 'https://www.youtube.com/watch?v=Ae_wUWaczXo',
    },
    {
      title: t('howDoICreateATasklists'),
      articleId: 4,
      icon: SubmissionIcon,
      video: 'https://www.youtube.com/watch?v=aY66fPfQUbc',
    },
    {
      title: t('howDoISubmit'),
      articleId: 5,
      icon: SubmitIcon,
      video: 'https://www.youtube.com/watch?v=h6pKy_CWEiE',
    },
    {
      title: t('howDoIPrint'),
      articleId: 6,
      icon: PrinterIcon,
      video: 'https://www.youtube.com/watch?v=k1cJrOv2oYk',
    },
    {
      title: t('howToAssign'),
      articleId: 7,
      icon: AssignIcon,
      video: 'https://www.youtube.com/watch?v=9ZPba_MdquM',
    },
  ];

  return {
    articles,
    articleItems,
  };
}
